import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";
import { i18n } from "../lib/i18n";

// Connects to data-controller="referral-org-picker"
export default class extends Controller {
  static targets = [
    "expandedPicker",
    "collapsedPicker",
    "listView",
    "selectedName",
    "selectedSource",
    "receivingOrgField",
    "hsdsOrgField",
    "servicesFrame",
    "inlineFormContainer",
    "addItLink",
  ];

  static values = {
    servicesUrl: String,
  };

  declare expandedPickerTarget: HTMLElement;
  declare collapsedPickerTarget: HTMLElement;
  declare listViewTarget: HTMLElement;
  declare hasListViewTarget: boolean;
  declare selectedNameTarget: HTMLElement;
  declare selectedSourceTarget: HTMLElement;
  declare receivingOrgFieldTarget: HTMLInputElement;
  declare hsdsOrgFieldTarget: HTMLInputElement;
  declare servicesFrameTarget: HTMLElement;

  declare hasReceivingOrgFieldTarget: boolean;
  declare hasHsdsOrgFieldTarget: boolean;
  declare hasServicesFrameTarget: boolean;
  declare inlineFormContainerTarget: HTMLElement;
  declare hasInlineFormContainerTarget: boolean;
  declare addItLinkTarget: HTMLElement;
  declare hasAddItLinkTarget: boolean;

  declare servicesUrlValue: string;

  selectOrg(event: Event) {
    const button = (event.currentTarget as HTMLElement);
    this.applySelection({
      hsdsOrgId: button.dataset.hsdsOrganizationId || "",
      receivingOrgId: button.dataset.receivingOrganizationId || "",
      orgName: button.dataset.organizationName || "",
      sourceType: button.dataset.sourceType || "",
      crmSameNetwork: button.dataset.crmSameNetwork === "true",
    });
  }

  selectFromDirectory(event: CustomEvent) {
    const { value, tomSelect } = event.detail;
    if (!value) return;

    const option = tomSelect?.options?.[value];
    if (!option) return;

    this.applySelection({
      hsdsOrgId: option.hsds_organization_id
        ? String(option.hsds_organization_id)
        : "",
      receivingOrgId: option.receiving_organization_id
        ? String(option.receiving_organization_id)
        : "",
      orgName: option.name || "",
      sourceType: option.source_type || "directory_org",
      crmSameNetwork: Boolean(option.crm_same_network),
    });
  }

  private applySelection({
    hsdsOrgId,
    receivingOrgId,
    orgName,
    sourceType,
    crmSameNetwork,
  }: {
    hsdsOrgId: string;
    receivingOrgId: string;
    orgName: string;
    sourceType: string;
    crmSameNetwork: boolean;
  }) {
    // Update hidden form fields
    if (this.hasReceivingOrgFieldTarget) {
      this.receivingOrgFieldTarget.value = receivingOrgId;
    }
    if (this.hasHsdsOrgFieldTarget) {
      this.hsdsOrgFieldTarget.value = hsdsOrgId;
    }

    // Update collapsed state display
    this.selectedNameTarget.textContent = orgName;
    this.selectedSourceTarget.textContent = this.sourceLabel(sourceType);

    // Reset inline-form state (also dispatches inline-form-hidden if open)
    this.resetToListView();

    // Toggle expanded/collapsed
    this.expandedPickerTarget.classList.add("hidden");
    this.collapsedPickerTarget.classList.remove("hidden");

    // Load services frame if HSDS org selected
    if (hsdsOrgId && this.servicesUrlValue) {
      const servicesFrame = document.querySelector(
        "turbo-frame#org_services_frame"
      ) as HTMLElement;
      if (servicesFrame) {
        const url = new URL(this.servicesUrlValue, document.baseURI);
        url.searchParams.set("hsds_organization_id", hsdsOrgId);
        servicesFrame.setAttribute("src", url.toString());
      }
    }

    // Full-history consent only applies to a full org in our own network. For a
    // directory-only, unclaimed, or cross-network receiver, hide the consent
    // toggle (and clear any full-history selection). Documents may still be sent
    // to any receiver.
    this.toggleConsentSection(crmSameNetwork);

    // Trigger auto-save if the form supports it
    this.dispatch("selected", { detail: { hsdsOrgId, receivingOrgId, orgName } });
  }

  // Show/hide the consent toggle, which only applies when the receiving org can
  // receive full neighbor history. Documents may be sent to any receiver, so
  // they are not toggled here. Located by id because it lives in the form
  // outside this controller's element.
  private toggleConsentSection(show: boolean) {
    const consent = document.getElementById("referral_consent_section");
    consent?.classList.toggle("hidden", !show);

    if (!show && consent) {
      consent
        .querySelectorAll<HTMLInputElement>("input[type=checkbox]")
        .forEach((checkbox) => {
          checkbox.checked = false;
        });
    }
  }

  change() {
    // Reset hidden fields
    if (this.hasReceivingOrgFieldTarget) {
      this.receivingOrgFieldTarget.value = "";
    }
    if (this.hasHsdsOrgFieldTarget) {
      this.hsdsOrgFieldTarget.value = "";
    }

    // Clear services frame
    const servicesFrame = document.querySelector(
      "turbo-frame#org_services_frame"
    ) as HTMLElement;
    if (servicesFrame) {
      servicesFrame.innerHTML = "";
      servicesFrame.removeAttribute("src");
    }

    // Reset inline-form state (also dispatches inline-form-hidden if open)
    this.resetToListView();

    // Restore the consent section now that no org is selected.
    this.toggleConsentSection(true);

    // Toggle expanded/collapsed
    this.collapsedPickerTarget.classList.add("hidden");
    this.expandedPickerTarget.classList.remove("hidden");
  }

  connect() {
    // Sync the inline-form input enabled state with the container's initial
    // visibility so server-rendered drafts (visible) and clean state (hidden)
    // both match what the form will submit.
    this.syncInlineFormInputs();
  }

  showInlineForm(event: Event) {
    event.preventDefault();
    if (this.hasListViewTarget) {
      this.listViewTarget.classList.add("hidden");
    }
    if (this.hasInlineFormContainerTarget) {
      this.inlineFormContainerTarget.classList.remove("hidden");
    }
    this.syncInlineFormInputs();
  }

  hideInlineForm(event: Event) {
    event.preventDefault();
    this.clearInlineFormInputs();
    this.resetToListView();
    // Explicitly clear the persisted `draft_new_organization` on the server
    // so it doesn't resurrect on the next render. We send a one-shot signal
    // because the auto-save endpoint otherwise leaves the draft alone.
    const form = this.element.closest("form") as HTMLFormElement | null;
    if (!form) return;

    const flag = document.createElement("input");
    flag.type = "hidden";
    flag.name = "referral[clear_inline_org_draft]";
    flag.value = "1";
    form.appendChild(flag);

    form.requestSubmit();

    flag.remove();
  }

  private resetToListView() {
    if (this.hasInlineFormContainerTarget) {
      this.inlineFormContainerTarget.classList.add("hidden");
    }
    if (this.hasListViewTarget) {
      this.listViewTarget.classList.remove("hidden");
    }
    this.syncInlineFormInputs();
  }

  // Disable inline-form inputs whenever the container is hidden so they don't
  // submit phantom values from a previous typing session.
  private syncInlineFormInputs() {
    if (!this.hasInlineFormContainerTarget) return;

    const hidden = this.inlineFormContainerTarget.classList.contains("hidden");
    this.inlineFormContainerTarget
      .querySelectorAll<HTMLInputElement>("input")
      .forEach((input) => {
        input.disabled = hidden;
      });
  }

  private clearInlineFormInputs() {
    if (!this.hasInlineFormContainerTarget) return;

    this.inlineFormContainerTarget
      .querySelectorAll<HTMLInputElement>("input")
      .forEach((input) => {
        input.value = "";
      });
  }

  private sourceLabel(sourceType: string): string {
    const labels: Record<string, string> = {
      preferred_partner: i18n.t("referral_org_picker.preferred_partner"),
      network_org: i18n.t("referral_org_picker.network_org"),
      directory_org: i18n.t("referral_org_picker.directory_org"),
      inline_created: i18n.t("referral_org_picker.inline_created"),
    };
    return labels[sourceType] || "";
  }
}
